import { Collapse, Form } from "antd"
import { type FormInstance } from "antd/es/form/hooks/useForm"
import React, { Suspense, useMemo } from "react"

import { FormChangedWatcher } from "../../../../../components/Forms/FormChangedWatcher/FormChangedWatcher.tsx"
import { useDateFormat } from "../../../../../hooks/use-date-format.ts"
import { showDocumentValidationError } from "../../../helpers/show-document-validation-error.tsx"
import type { DocumentScopeSectionProjectTypes } from "../../../types/document.types.ts"
import type { TemplateDocumentFormData } from "../../../types/template-document-form-data.tsx"
import { type DocumentTemplateBlock } from "../../../types/types.ts"
import { buildBlockVariants } from "../build-block-variants.tsx"
import { TemplateDocumentFormLabel } from "./TemplateDocumentFormLabel/TemplateDocumentFormLabel.tsx"

interface TemplateDocumentFormProps {
  form?: FormInstance<TemplateDocumentFormData>
  saving: boolean
  initialValues?: Partial<TemplateDocumentFormData>
  projectTypes: DocumentScopeSectionProjectTypes
  onSubmit: (payload: TemplateDocumentFormData) => Promise<void>
}

export function TemplateDocumentForm({
  initialValues,
  form: innerForm,
  saving,
  projectTypes,
  onSubmit
}: TemplateDocumentFormProps) {
  const { normalizeDate } = useDateFormat()

  const [form] = Form.useForm<TemplateDocumentFormData>(innerForm)

  const onFinishHandler = async ({
    completion_date,
    start_date,
    ...values
  }: TemplateDocumentFormData) => {
    try {
      await onSubmit({
        ...values,
        completion_date: normalizeDate(completion_date),
        start_date: normalizeDate(start_date)
      })
    } catch (e) {
      console.log("Template document form error:", e)
    }
  }

  const variants = useMemo(
    () => buildBlockVariants({ form, projectTypes }),
    [form, projectTypes]
  )

  const blocks =
    Form.useWatch(
      (values) =>
        values?.blocks?.map(
          ({ id, block_type, order, config: { title, alignment } = {} }) => ({
            id,
            order,
            block_type,
            config: { title, alignment }
          })
        ),
      form
    ) ??
    form.getFieldValue("blocks") ??
    initialValues?.blocks ??
    []

  const removeBlock = (id: string) => {
    const blocks = form.getFieldValue("blocks") as DocumentTemplateBlock[]

    form.setFields([
      {
        name: "blocks",
        touched: true,
        value: blocks.filter((b) => b.id !== id)
      }
    ])
  }

  return (
    <Form
      className={"with-default-error overflow-auto tailwind-scroll"}
      validateTrigger={["onSubmit", "onChange"]}
      layout="vertical"
      onFinish={onFinishHandler}
      onFinishFailed={(errorInfo) =>
        showDocumentValidationError(form, errorInfo)
      }
      form={form}
      initialValues={initialValues}
      scrollToFirstError={{
        behavior: "smooth",
        block: "center",
        scrollMode: "always"
      }}
      disabled={saving}
    >
      <FormChangedWatcher<TemplateDocumentFormData>
        form={form}
        saving={saving}
      />

      <Collapse
        collapsible={"icon"}
        className={"bg-transparent border-x-0"}
        defaultActiveKey={initialValues?.blocks?.map((b) => b?.id)}
        items={blocks.map((block, i) => {
          const data = variants?.[block?.block_type]
          if (!data) return { key: block?.id, label: "No data" }
          const component = data?.component

          return {
            key: block?.id,
            showArrow: false,
            label: (
              <TemplateDocumentFormLabel
                id={block?.id}
                index={i}
                form={form}
                label={data?.label}
                alignment={data?.alignment}
                required={data?.required}
                titleReadonly={data?.titleReadonly}
                columns={data?.columns}
                onRemove={removeBlock}
              />
            ),
            className: "mb-3 border-2 rounded-lg border-border",
            children: (
              <Suspense fallback={"Loading"}>
                {typeof component === "function"
                  ? component({ block, disabled: saving }, i)
                  : component}
              </Suspense>
            )
          }
        })}
      />

      {/* register in the form */}
      <Form.Item<TemplateDocumentFormData> name="blocks">
        <select className={"invisible"} />
      </Form.Item>
    </Form>
  )
}
